<script setup lang="ts">
import { downloadArrayBuffer } from "@/components/files/DrawingList/downloadFile";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRDropdownItem from "@/components/UI/DRDropdownItem.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { localDrawingRepository } from "@/repositories/local-drawing.repository";
import type { DRBackup } from "@/types";
import router from "@/router";
import { ref } from "vue";
import { useTranslation } from "i18next-vue";

const { t } = useTranslation();

const props = defineProps<{
  backup: DRBackup;
}>();

const emits = defineEmits<{
  (e: "backup-applied"): void;
}>();

const formatDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString("fr-FR", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};
const formatDateCode = (date: Date | string) => {
  return new Date(date).toISOString().replace(/[-:T]/g, "").slice(0, 15);
};

const showSpinner = ref(false);

async function downloadBackup() {
  if (!props.backup.drawingId) {
    return;
  }
  showSpinner.value = true;
  const createdAtStr = formatDateCode(props.backup.createdAt);
  const fileName = `DR-${props.backup.drawingId}-${createdAtStr}`;
  try {
    if (props.backup.vsf) {
      // DEPRECATED
      downloadArrayBuffer(props.backup.vsf, fileName + "vsfx", "vsfx");
    } else if (props.backup.dwg) {
      downloadArrayBuffer(props.backup.dwg, fileName + "dwg", "dwg");
    }
  } catch (e: any) {
    console.error("Error downloading backup:", e);
    throw e;
  } finally {
    showSpinner.value = false;
  }
}

const confirmApplyBackup = ref(false);

const applyBackup = () => {
  confirmApplyBackup.value = true;
};
const cancelApplyBackup = () => {
  confirmApplyBackup.value = false;
};

async function confirmApplyBackupHandler() {
  confirmApplyBackup.value = false;
  if (!props.backup.drawingId || !props.backup.dwg) return;

  showSpinner.value = true;
  try {
    // Save backup to local storage as the new draft
    await localDrawingRepository.saveDraft(
      props.backup.drawingId,
      props.backup.dwg,
      false,
    );

    // Navigate to previous page to close drawing
    router.back();

    emits("backup-applied");
  } catch (e: any) {
    console.error("Error applying backup:", e);
    alert(t("canvas:settings.backups.applyError"));
  } finally {
    showSpinner.value = false;
  }
}
</script>

<template>
  <div
    class="d-flex align-items-center justify-content-between canvas-hoverable text-white rounded py-1 px-2 gap-2"
  >
    <div class="col-auto"><PhosphorIcon name="floppy-disk" :size="16" /></div>
    <div class="col text-truncate">
      <small>
        {{
          $t("canvas:settings.backups.text", {
            date: formatDate(backup.createdAt),
          })
        }}
      </small>
    </div>
    <el-dropdown trigger="click" @click.stop.prevent>
      <DRBtnIcon
        phosphor-icon="dots-three"
        variant="flat-white"
        :size="25"
        @click.stop.prevent
        @dblclick.stop.prevent
      />

      <template #dropdown>
        <el-dropdown-menu>
          <DRDropdownItem
            @select="downloadBackup()"
            phosphor-icon="download-simple"
            :label="$t('canvas:settings.backups.download')"
          />
          <DRDropdownItem
            v-if="props.backup.dwg"
            @select="applyBackup()"
            phosphor-icon="file-arrow-down"
            :label="$t('canvas:settings.backups.apply')"
          />
        </el-dropdown-menu>
      </template>
    </el-dropdown>
  </div>
  <ConfirmationModal
    v-model="confirmApplyBackup"
    :message="$t('canvas:settings.backups.confirmApply')"
    @confirm="confirmApplyBackupHandler"
    @cancel="cancelApplyBackup"
  />
</template>

<style lang="scss" scoped>
.canvas-hoverable {
  background-color: var(--canvas-bg);
  transition: background-color 0.1s ease-in-out;

  &:hover {
    background-color: var(--canvas-border);
  }
}
</style>
